import { useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
  alpha,
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import BlockRoundedIcon from '@mui/icons-material/BlockRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import FilterListRoundedIcon from '@mui/icons-material/FilterListRounded';
import BusinessRoundedIcon from '@mui/icons-material/BusinessRounded';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { DataTable } from '../../components/common/DataTable';
import { StatusChip } from '../../components/common/StatusChip';
import { ConfirmDialog } from '../../components/common/ConfirmDialog';
import { tenantService } from '../../services/tenant.service';
import { planService } from '../../services/plan.service';
import type { Plan, Tenant } from '../../types';
import { TenantStatus } from '../../types';
import { formatINR } from '../../lib/currency';

interface TenantFormValues {
  name: string;
  slug: string;
  planId: string;
  employeeLimit: number;
  adminName: string;
  adminEmail: string;
  adminPassword: string;
}

const emptyForm: TenantFormValues = {
  name: '',
  slug: '',
  planId: '',
  employeeLimit: 50,
  adminName: '',
  adminEmail: '',
  adminPassword: '',
};

function slugify(v: string) {
  return v.toLowerCase().trim().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-').replace(/-+/g, '-');
}

export function TenantsPage() {
  const [dialogOpen,    setDialogOpen]    = useState(false);
  const [editing,       setEditing]       = useState<Tenant | null>(null);
  const [deleteTarget,  setDeleteTarget]  = useState<Tenant | null>(null);
  const [search,        setSearch]        = useState('');
  const [statusFilter,  setStatusFilter]  = useState<'ALL' | TenantStatus>('ALL');
  const [page,          setPage]          = useState(0);
  const [rowsPerPage,   setRowsPerPage]   = useState(10);
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['tenants', page, rowsPerPage, statusFilter],
    queryFn: () => tenantService.getAll({
      page: page + 1, limit: rowsPerPage,
      ...(statusFilter !== 'ALL' ? { status: statusFilter } : {}),
    }),
  });

  const { data: plans = [] } = useQuery({
    queryKey: ['plans'],
    queryFn: () => planService.getAll(),
  });

  const { register, handleSubmit, reset, setValue, watch } = useForm<TenantFormValues>({ defaultValues: emptyForm });
  const watchedName = watch('name');

  const createMutation = useMutation({
    mutationFn: tenantService.create,
    onSuccess: () => { toast.success('Tenant created. Share the admin email and password so they can log in.'); qc.invalidateQueries({ queryKey: ['tenants'] }); closeDialog(); },
    onError: (e: Error) => toast.error(e.message || 'Failed to create tenant'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, values }: { id: string; values: Partial<TenantFormValues> }) => tenantService.update(id, values),
    onSuccess: () => { toast.success('Tenant updated'); qc.invalidateQueries({ queryKey: ['tenants'] }); closeDialog(); },
    onError: (e: Error) => toast.error(e.message || 'Failed to update tenant'),
  });

  const statusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: TenantStatus }) => tenantService.updateStatus(id, status),
    onSuccess: () => { toast.success('Status updated'); qc.invalidateQueries({ queryKey: ['tenants'] }); },
    onError: (e: Error) => toast.error(e.message),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => tenantService.delete(id),
    onSuccess: () => { toast.success('Tenant deactivated'); qc.invalidateQueries({ queryKey: ['tenants'] }); setDeleteTarget(null); },
    onError: (e: Error) => toast.error(e.message),
  });

  function closeDialog() { setDialogOpen(false); setEditing(null); reset(emptyForm); }

  function openCreate() {
    setEditing(null);
    reset({ ...emptyForm, planId: (plans as Plan[])[0]?.id ?? '' });
    setDialogOpen(true);
  }

  function openEdit(t: Tenant) {
    setEditing(t);
    reset({ name: t.name, slug: t.slug, planId: t.planId, employeeLimit: t.employeeLimit });
    setDialogOpen(true);
  }

  function onSubmit(values: TenantFormValues) {
    if (editing) {
      updateMutation.mutate({
        id: editing.id,
        values: {
          name: values.name.trim(),
          slug: slugify(values.slug || values.name),
          planId: values.planId,
          employeeLimit: Number(values.employeeLimit),
        },
      });
      return;
    }

    createMutation.mutate({
      name: values.name.trim(),
      slug: slugify(values.slug || values.name),
      planId: values.planId,
      employeeLimit: Number(values.employeeLimit),
      adminName: values.adminName.trim(),
      adminEmail: values.adminEmail.trim(),
      adminPassword: values.adminPassword,
    });
  }

  const filteredRows = useMemo(() => {
    const rows = data?.data ?? [];
    const q = search.trim().toLowerCase();
    return q ? rows.filter((r) => r.name.toLowerCase().includes(q) || r.slug.includes(q)) : rows;
  }, [data?.data, search]);

  const saving = createMutation.isPending || updateMutation.isPending;

  const columns = [
    {
      id: 'name', label: 'Company', minWidth: 220,
      render: (row: Tenant) => (
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Avatar sx={{ width: 38, height: 38, bgcolor: '#E8EEF8', color: '#1D4ED8', fontSize: '0.8rem', fontWeight: 700, borderRadius: '10px' }}>
            {row.name.slice(0, 2).toUpperCase()}
          </Avatar>
          <Box>
            <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: 'text.primary', lineHeight: 1.3 }}>{row.name}</Typography>
            <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', fontFamily: 'monospace' }}>{row.slug}</Typography>
          </Box>
        </Stack>
      ),
    },
    {
      id: 'plan', label: 'Plan',
      render: (row: Tenant) => (
        <Chip label={row.plan?.name ?? '—'} size="small" sx={{ fontWeight: 600, bgcolor: '#E8EEF8', color: '#1E4BB8', border: 'none', borderRadius: '6px' }} />
      ),
    },
    {
      id: 'employees', label: 'Seats used',
      render: (row: Tenant) => {
        const pct = row.employeeLimit > 0 ? Math.min(100, ((row.employeeCount ?? 0) / row.employeeLimit) * 100) : 0;
        return (
          <Stack sx={{ minWidth: 100 }} spacing={0.5}>
            <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: 'text.primary' }}>
              {row.employeeCount ?? 0}
              <Typography component="span" sx={{ color: 'text.secondary', fontWeight: 400 }}>
                {' '}/ {row.employeeLimit === -1 ? '∞' : row.employeeLimit}
              </Typography>
            </Typography>
            {row.employeeLimit !== -1 && pct > 0 && (
              <LinearProgress variant="determinate" value={pct} sx={{ height: 4, borderRadius: 4, bgcolor: '#EEF1F6' }} color={pct > 90 ? 'error' : 'primary'} />
            )}
          </Stack>
        );
      },
    },
    { id: 'status', label: 'Status', render: (row: Tenant) => <StatusChip status={row.status} /> },
    {
      id: 'createdAt', label: 'Created',
      render: (row: Tenant) => (
        <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary' }}>{format(new Date(row.createdAt), 'dd MMM yyyy')}</Typography>
      ),
    },
    {
      id: 'actions', label: '', align: 'right' as const,
      render: (row: Tenant) => (
        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
          <Tooltip title="Edit tenant">
            <IconButton size="small" onClick={() => openEdit(row)} sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main', bgcolor: (t) => alpha(t.palette.primary.main, 0.08) } }}>
              <EditRoundedIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          {row.status === TenantStatus.ACTIVE ? (
            <Tooltip title="Suspend">
              <IconButton size="small" onClick={() => statusMutation.mutate({ id: row.id, status: TenantStatus.SUSPENDED })} sx={{ color: 'text.secondary', '&:hover': { color: 'warning.main', bgcolor: '#FFFBEB' } }}>
                <BlockRoundedIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          ) : (
            <Tooltip title="Activate">
              <IconButton size="small" onClick={() => statusMutation.mutate({ id: row.id, status: TenantStatus.ACTIVE })} sx={{ color: 'text.secondary', '&:hover': { color: 'success.main', bgcolor: '#ECFDF5' } }}>
                <CheckCircleRoundedIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Deactivate">
            <IconButton size="small" onClick={() => setDeleteTarget(row)} sx={{ color: 'text.secondary', '&:hover': { color: 'error.main', bgcolor: '#FEF2F2' } }}>
              <DeleteOutlineRoundedIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ];

  return (
    <Box>
      <Stack direction="row" justifyContent="flex-end" mb={2}>
        <Button
          variant="contained"
          startIcon={<AddRoundedIcon />}
          onClick={openCreate}
        >
          Add Tenant
        </Button>
      </Stack>

      {/* ── Toolbar ──────────────────────────────────────── */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 1.5,
          alignItems: { md: 'center' },
          p: 2,
          mb: 2,
          borderRadius: '14px',
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <TextField
          placeholder="Search company or slug…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ maxWidth: { md: 340 } }}
          fullWidth
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchRoundedIcon sx={{ fontSize: 18 }} /></InputAdornment>,
          }}
        />
        <TextField
          select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value as 'ALL' | TenantStatus); setPage(0); }}
          sx={{ minWidth: 160 }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><FilterListRoundedIcon sx={{ fontSize: 16 }} /></InputAdornment>,
          }}
        >
          <MenuItem value="ALL">All statuses</MenuItem>
          <MenuItem value={TenantStatus.ACTIVE}>Active</MenuItem>
          <MenuItem value={TenantStatus.SUSPENDED}>Suspended</MenuItem>
          <MenuItem value={TenantStatus.INACTIVE}>Inactive</MenuItem>
        </TextField>
        <Box flex={1} />
        <Chip
          icon={<BusinessRoundedIcon fontSize="small" />}
          label={`${data?.total ?? 0} tenants`}
          variant="outlined"
          sx={{ fontWeight: 600, borderColor: 'divider', color: 'text.secondary' }}
        />
      </Box>

      <DataTable
        columns={columns}
        rows={filteredRows}
        getRowId={(row) => row.id}
        isLoading={isLoading}
        page={page}
        rowsPerPage={rowsPerPage}
        totalCount={data?.total ?? 0}
        onPageChange={setPage}
        onRowsPerPageChange={(v) => { setRowsPerPage(v); setPage(0); }}
        emptyMessage="No tenants found"
        emptySubtext="Create your first tenant account to get started."
      />

      {/* ── Create / Edit dialog ─────────────────────────── */}
      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogTitle>{editing ? 'Edit Tenant' : 'New Tenant'}</DialogTitle>
          <Divider />
          <DialogContent>
            <Stack spacing={2.5} pt={1}>
              <TextField
                label="Company name"
                fullWidth
                required
                {...register('name', { required: true })}
                onChange={(e) => { setValue('name', e.target.value); if (!editing) setValue('slug', slugify(e.target.value)); }}
                helperText={!editing && watchedName ? `Slug: ${slugify(watchedName)}` : undefined}
              />
              <TextField
                label="URL slug"
                fullWidth
                required
                placeholder="acme-corp"
                {...register('slug', { required: true })}
                helperText="Lowercase letters, numbers and hyphens only"
              />
              <TextField
                select
                label="Subscription plan"
                fullWidth
                required
                value={watch('planId')}
                onChange={(e) => setValue('planId', e.target.value)}
              >
                {(plans as Plan[]).map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    <Stack direction="row" justifyContent="space-between" width="100%" alignItems="center">
                      <Typography fontSize="0.875rem" fontWeight={600}>{p.name}</Typography>
                      <Typography fontSize="0.8rem" color="text.secondary">
                        {formatINR(p.monthlyPrice)}/mo · {p.employeeLimit === -1 ? 'Unlimited' : `${p.employeeLimit} seats`}
                      </Typography>
                    </Stack>
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Employee seat limit"
                type="number"
                fullWidth
                inputProps={{ min: 1 }}
                {...register('employeeLimit', { valueAsNumber: true, required: true })}
                helperText="Maximum number of employees for this tenant"
              />

              {!editing && (
                <>
                  <Typography variant="subtitle2" fontWeight={700} pt={0.5}>
                    Tenant admin login
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: '-12px !important' }}>
                    This person will sign in at /login and manage the company account.
                  </Typography>
                  <TextField
                    label="Admin name"
                    fullWidth
                    required
                    {...register('adminName', { required: !editing })}
                  />
                  <TextField
                    label="Admin email"
                    type="email"
                    fullWidth
                    required
                    placeholder="admin@company.com"
                    {...register('adminEmail', { required: !editing })}
                  />
                  <TextField
                    label="Admin password"
                    type="password"
                    fullWidth
                    required
                    helperText="Minimum 8 characters"
                    {...register('adminPassword', { required: !editing, minLength: 8 })}
                  />
                </>
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeDialog} color="inherit" variant="outlined">Cancel</Button>
            <Button type="submit" variant="contained" disabled={saving}>
              {saving ? 'Saving…' : editing ? 'Save Changes' : 'Create Tenant'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <ConfirmDialog
        open={!!deleteTarget}
        title="Deactivate tenant?"
        message={`This will set "${deleteTarget?.name}" to Inactive. Their data will be preserved and can be re-activated later.`}
        confirmLabel="Deactivate"
        confirmColor="error"
        isLoading={deleteMutation.isPending}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
      />
    </Box>
  );
}
